import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../Common/Layout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import axios from "axios";
import { Toast } from "primereact/toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "../utils/dateFormat";
interface CoverageRow {
  id: number;
  name: string;
  limit: string;
  deductible: string;
}

interface VehicleInformationProps {
  onNext?: (data?: any) => void;
  onPrevious?: () => void;
  workflowData?: any;
  currentStep?: number;
  totalSteps?: number;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  selectedState?: string;
  insuredId?: string;
  loading?: boolean;
}

interface VehicleData {
  id: number;
  isExpanded: boolean;
  vin: string;
  year: string;
  make: string;
  model: string;
  purchaseDate: string;
  dailyMileage: string;
  annualMileage: string;
  comprehensiveDeductible: string;
  collisionDeductible: string;
  rentalLimit: string;
  rentalDeductible: string;
  towingLaborLimit: string;
  towingLaborDeductible: string;
  customEquipmentLimit: string;
  customEquipmentDeductible: string;
  gapLimit: string;
  gapDeductible: string;
  // Added fields
  usage?: string;
  milesToWork?: string;
  assignedDriverId?: string;
  telematics?: string;
  // New fields for extra coverages
  safetyGlassCoverage?: string;
}

export default function VehicleInformation(props: VehicleInformationProps = {}) {
  // ...existing code...

  // Reusable field renderers
  const renderAssignedDriverId = (vehicle: VehicleData) => (
    <div className="form-group">
      <label htmlFor={`assignedDriverId-${vehicle.id}`}>Assigned Driver ID</label>
      <input
        type="number"
        id={`assignedDriverId-${vehicle.id}`}
        name="assignedDriverId"
        value={vehicle.assignedDriverId || ""}
        onChange={(e) => {
          let val = e.target.value;
          if (val === "") {
            handleInputChange(vehicle.id, e);
          } else {
            let num = Number(val);
            if (!isNaN(num)) {
              if (num < 1) num = 1;
              if (num > 6) num = 6;
              e.target.value = String(num);
              handleInputChange(vehicle.id, e);
            }
          }
        }}
        className="form-input"
        min={1}
        max={6}
        placeholder="Enter assigned driver ID (1-6)"
      />
    </div>
  );
  const renderTelematics = (vehicle: VehicleData) => (
    <div className="form-group">
      <label htmlFor={`telematics-${vehicle.id}`}>Telematics</label>
      <select
        id={`telematics-${vehicle.id}`}
        name="telematics"
        value={vehicle.telematics || ""}
        onChange={(e) => handleInputChange(vehicle.id, e)}
        className="form-input compact-select"
      >
        <option value="">Select option</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  );

  const renderUsageDropdown = (vehicle: VehicleData) => (
    <div className="form-group">
      <label htmlFor={`usage-${vehicle.id}`}>Usage</label>
      <select
        id={`usage-${vehicle.id}`}
        name="usage"
        className="form-input compact-select"
        value={vehicle.usage ?? ""}
        onChange={(e) => handleInputChange(vehicle.id, e)}
      >
        <option value="">Select</option>
        <option value={0}>Artisan Use</option>
        <option value={1}>Business Use</option>
        <option value={4}>Farm</option>
        <option value={7}>Pleasure</option>
        <option value={3}>Work School</option>
      </select>
    </div>
  );
  // Vehicle makes list
  const VEHICLE_MAKES = [
     "ACURA",
    "ALFA ROMEO",
    "ASTON MARTIN",
    "AUDI",
    "AUTOMOBILI PININFARI",
    "BENTLEY",
    "BMW",
    "BUGATTI",
    "BUGATTI RIMAC",
    "BUICK",
    "CADILLAC",
    "CHEVROLET",
    "CHRYSLER",
    "DODGE",
    "FERRARI",
    "FIAT",
    "FORD",
    "GENESIS",
    "GMC",
    "HONDA",
    "HYUNDAI",
    "INEOS AUTOMOTIVE LIM",
    "INFINITI",
    "JAGUAR",
    "JEEP",
    "KARMA",
    "KIA",
    "LAMBORGHINI",
    "LAND ROVER",
    "LEXUS",
    "LINCOLN",
    "LOTUS",
    "LUCID MOTORS",
    "MASERATI",
    "MAZDA",
    "MCLAREN",
    "MERCEDES BENZ",
    "MINI",
    "MITSUBISHI",
    "NISSAN",
    "POLESTAR",
    "PORSCHE",
    "RAM",
    "RIVIAN",
    "ROLLS ROYCE",
    "SUBARU",
    "TESLA",
    "TOYOTA",
    "VINFAST",
    "VOLKSWAGEN",
    "VOLVO",
    "ZEEKR"
  ];

  const { onNext, onPrevious, workflowData, currentStep, totalSteps, isFirstStep, isLastStep, selectedState, insuredId, loading } = props;

  // Determine policy type and non-owner status
  const policyType = workflowData?.step1?.policyType || "";
  const isNonOwnerPolicy = policyType === "Nonowner";
  const toast = React.useRef<Toast>(null);
  const navigate = useNavigate();
  const [nextVehicleId, setNextVehicleId] = useState(2);
  const [vehicleLoading, setVehicleLoading] = useState<boolean>(false);
  const [configPurchaseDate, setConfigPurchaseDate] = useState<string>("");
  const [vehicleConfiguration, setVehicleConfiguration] = useState<any>(null);
  const vehicleConfigurationRef = useRef<any>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expandTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasScrolledToTopRef = useRef<boolean>(false);
  const isUserInteractingRef = useRef<boolean>(false);
  const scrollPositionRef = useRef<number>(0);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [vehicleModels, setVehicleModels] = useState<{ [vehicleId: number]: string[] }>({});
  const [vehicles, setVehicles] = useState<VehicleData[]>([
    {
      id: 1,
      isExpanded: true,
      vin: "",
      year: "",
      make: "",
      model: "",
      purchaseDate: "",
      dailyMileage: "10",
      annualMileage: "12000",
      comprehensiveDeductible: "",
      collisionDeductible: "",
      rentalLimit: "",
      rentalDeductible: "",
      towingLaborLimit: "",
      towingLaborDeductible: "",
      customEquipmentLimit: "",
      customEquipmentDeductible: "",
      gapLimit: "",
      gapDeductible: "",
      usage: "",
      milesToWork: "",
      assignedDriverId: "",
      telematics: "",
      safetyGlassCoverage: "",
    },
  ]);

  useEffect(() => {
    // Only scroll to top once on initial mount
    if (!hasScrolledToTopRef.current) {
      const layoutContent = document.querySelector(".layout-content");
      if (layoutContent) {
        layoutContent.scrollTo({ top: 0, behavior: "auto" });
      }
      window.scrollTo({ top: 0, behavior: "auto" });
      hasScrolledToTopRef.current = true;
    }

    // Cleanup timeouts on unmount
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);
    };
  }, []);

  const [coverageData, setCoverageData] = useState<CoverageRow[]>([
    { id: 1, name: "Comprehensive", limit: "ACV", deductible: "" },
    { id: 2, name: "Collision", limit: "ACV", deductible: "" },
    { id: 3, name: "Rental", limit: "", deductible: "" },
    { id: 4, name: "Towing and Labor", limit: "", deductible: "" },
    { id: 5, name: "Custom Equipment", limit: "", deductible: "" },
    { id: 6, name: "GAP", limit: "", deductible: "" },
  ]);
  useEffect(() => {
    // Only run this effect if workflowData is not undefined (wait for parent to provide it)
    if (!workflowData || Object.keys(workflowData).length === 0) {
      // Do not call API if workflowData is not ready
      return;
    }
    if (insuredId && !isNonOwnerPolicy && (!workflowData?.step3 || workflowData?.step3.length === 0)) {
      const timer = setTimeout(() => {
        getVehiclesListById();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [insuredId, isNonOwnerPolicy, workflowData]);
  useEffect(() => {
    if (
      selectedState &&
      !isNonOwnerPolicy
      // &&(!workflowData?.step3 || workflowData?.step3.length === 0)
    ) {
      getVehicleConfigurationDetails();
    }
  }, [selectedState, isNonOwnerPolicy, workflowData?.step3]);

  useEffect(() => {
    if (!workflowData) return;

    if (isNonOwnerPolicy) {
      setVehicles([]);
      return;
    }

    if (workflowData.step3 && workflowData.step3.length > 0) {
      // Format purchase dates and merge with configuration when loading from workflowData
      const formattedVehicles = workflowData.step3.map((vehicle: VehicleData) => ({
        ...vehicle,
        make: vehicle.make ? vehicle.make.toUpperCase() : "",
        purchaseDate: vehicle.purchaseDate
          ? formatDate(vehicle.purchaseDate)
          : vehicleConfiguration?.purchaseDate
            ? formatDate(vehicleConfiguration.purchaseDate)
            : "",
        dailyMileage: vehicle.dailyMileage || vehicleConfiguration?.dailyMileage?.toString() || "10",
        annualMileage: vehicle.annualMileage || vehicleConfiguration?.annualMileage?.toString() || "12000",
        comprehensiveDeductible: vehicle.comprehensiveDeductible || vehicleConfiguration?.comprehensiveDeductible || "",
        collisionDeductible: vehicle.collisionDeductible || vehicleConfiguration?.collisionDeductible || "",
        rentalLimit: vehicle.rentalLimit || vehicleConfiguration?.rentalLimit || "",
        towingLaborLimit: vehicle.towingLaborLimit || vehicleConfiguration?.towingAndLaborLimit || "",
        customEquipmentLimit: vehicle.customEquipmentLimit || vehicleConfiguration?.customEquipmentLimit || "",
        gapLimit: vehicle.gapLimit || vehicleConfiguration?.gap || "",
        usage: vehicle.usage ?? vehicleConfiguration?.usage ?? "",
        milesToWork: vehicle.milesToWork || vehicleConfiguration?.milesToWork || "",
        assignedDriverId: vehicle.assignedDriverId || "",
        telematics: vehicle.telematics || vehicleConfiguration?.telematics || "",
        safetyGlassCoverage: vehicle.safetyGlassCoverage || vehicleConfiguration?.safetyGlassCoverage || "",
      }));
      setVehicles(formattedVehicles);

      // Fetch models for vehicles that have year and make; decode VIN if year/make/model are missing
      formattedVehicles.forEach((vehicle: VehicleData) => {
        if (vehicle.year && vehicle.make) {
          fetchModels(vehicle.id, vehicle.year, vehicle.make);
        } else if (vehicle.vin && vehicle.vin.length === 17 && (!vehicle.year || !vehicle.make || !vehicle.model)) {
          decodeVINByString(vehicle.id, vehicle.vin);
        }
      });

      // Update nextVehicleId based on loaded vehicles
      const maxId = Math.max(...formattedVehicles.map((v: VehicleData) => v.id));
      setNextVehicleId(maxId + 1);
    } else if (workflowData.step3 && workflowData.step3.length === 0 && !workflowData.isNonOwnerPolicy) {
      // If step3 is empty array but non-owner policy is NOT selected, ensure we have a default vehicle
      if (vehicles.length === 0) {
        setVehicles([
          {
            id: 1,
            isExpanded: true,
            vin: "",
            year: "",
            make: "",
            model: "",
            purchaseDate: vehicleConfiguration?.purchaseDate ? vehicleConfiguration.purchaseDate.split("T")[0] : "",
            dailyMileage: vehicleConfiguration?.dailyMileage?.toString() || "10",
            annualMileage: vehicleConfiguration?.annualMileage?.toString() || "12000",
            comprehensiveDeductible: vehicleConfiguration?.comprehensiveDeductible || "",
            collisionDeductible: vehicleConfiguration?.collisionDeductible || "",
            rentalLimit: vehicleConfiguration?.rentalLimit || "",
            rentalDeductible: "",
            towingLaborLimit: vehicleConfiguration?.towingAndLaborLimit || "",
            towingLaborDeductible: "",
            customEquipmentLimit: vehicleConfiguration?.customEquipmentLimit || "",
            customEquipmentDeductible: "",
            gapLimit: vehicleConfiguration?.gap || "",
            gapDeductible: "",
            usage: vehicleConfiguration?.usage || "",
            milesToWork: vehicleConfiguration?.milesToWork || "",
            assignedDriverId: "",
            telematics: vehicleConfiguration?.telematics || "",
            safetyGlassCoverage: vehicleConfiguration?.safetyGlassCoverage || "",
          },
        ]);
        setNextVehicleId(2);
      }
    }
  }, [workflowData, vehicleConfiguration]);

  const getVehicleConfigurationDetails = () => {
    // setVehicleLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/vehicleinformationconfiguration?state=${selectedState}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const config = response.data;

        // Store full configuration
        setVehicleConfiguration(config);
        vehicleConfigurationRef.current = config;

        // Store purchase date from config
        if (config.purchaseDate) {
          setConfigPurchaseDate(config.purchaseDate.split("T")[0]);
        }

        // Update vehicles state with configuration values for ALL vehicles
        setVehicles((prev) =>
          prev.map((vehicle) => ({
            ...vehicle,
            purchaseDate: vehicle.purchaseDate || (config.purchaseDate ? config.purchaseDate.split("T")[0] : ""),
            dailyMileage: !vehicle.dailyMileage || vehicle.dailyMileage === "10" ? config.dailyMileage?.toString() || "10" : vehicle.dailyMileage,
            annualMileage:
              !vehicle.annualMileage || vehicle.annualMileage === "12000" ? config.annualMileage?.toString() || "12000" : vehicle.annualMileage,
            comprehensiveDeductible: vehicle.comprehensiveDeductible || config.comprehensiveDeductible || "",
            collisionDeductible: vehicle.collisionDeductible || config.collisionDeductible || "",
            rentalLimit: vehicle.rentalLimit || config.rentalLimit || "",
            towingLaborLimit: vehicle.towingLaborLimit || config.towingAndLaborLimit || "",
            customEquipmentLimit: vehicle.customEquipmentLimit || config.customEquipmentLimit || "",
            gapLimit: vehicle.gapLimit || config.gap || "",
            usage: vehicle.usage ?? config.usage ?? "",
            milesToWork: vehicle.milesToWork || config.milesToWork || "",
            assignedDriverId: vehicle.assignedDriverId || "",
            telematics: vehicle.telematics || config.telematics || "",
            safetyGlassCoverage: vehicle.safetyGlassCoverage || config.safetyGlassCoverage || "",
          })),
        );
      })
      .catch((error: any) => {
        setVehicleLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while getting  the vehicle configuration details!",
            life: 3000,
          });
        }
      });
  };
  // Helper function to fetch models based on year and make
  const fetchModels = async (vehicleId: number, year: string, make: string) => {
    if (!year || !make) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ACE_API_Url}/api/ace/model?year=${year}&make=${encodeURIComponent(make)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        },
      );
      const data: string[] = await response.json();

      if (Array.isArray(data) && data.length > 0) {
        const uniqueModels: string[] = Array.from(new Set(data)).sort();
        setVehicleModels((prev) => ({
          ...prev,
          [vehicleId]: uniqueModels,
        }));
      } else {
        setVehicleModels((prev) => ({
          ...prev,
          [vehicleId]: [],
        }));
      }
    } catch (error) {
      console.error("Error fetching models:", error);
      setVehicleModels((prev) => ({
        ...prev,
        [vehicleId]: [],
      }));
    }
  };

  // Helper function to decode VIN
  const decodeVINByString = async (vehicleId: number, vin: string) => {
    if (!vin || vin.length !== 17) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ACE_API_Url}/api/ace/vindetails?vin=${vin}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        },
      );
      const data = await response.json();

      const year = data.year ? String(data.year) : "";
      const make = data.maker || "";
      const model = data.model || "";

      if (!year && !make && !model) {
        return;
      }

      setVehicles((prev) =>
        prev.map((v) =>
          v.id === vehicleId
            ? {
                ...v,
                year: year || v.year,
                make: make ? make.toUpperCase() : v.make,
                model: model || v.model,
              }
            : v,
        ),
      );

      // Fetch models for the decoded make and year
      if (year && make) {
        fetchModels(vehicleId, year, make);
      }
    } catch (error) {
      console.error("Error decoding VIN:", error);
    }
  };

  const getVehiclesListById = () => {
    setVehicleLoading(true);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/vehicles?insuredDatabaseId=${insuredId}&ncToken=${localStorage.getItem("NCToken")}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
    })
      .then((response: any) => {
        const vehicleList = response.data?.value || [];
        // Map ALL vehicles from API (replace default vehicle)
        const mappedVehicles = vehicleList.map((apiVehicle: any, index: number) => ({
          id: index + 1, // start from 1
          isExpanded: index === 0, // Expand first vehicle
          vin: apiVehicle.vin || "",
          year: apiVehicle.year ? apiVehicle.year.toString() : "",
          make: (() => {
            const raw = apiVehicle.make ? apiVehicle.make.toUpperCase() : "";
            return VEHICLE_MAKES.includes(raw) ? raw : "";
          })(),
          // make: apiVehicle.make ? apiVehicle.make.toLowerCase() : "",

          model: apiVehicle.model || "",
          type: apiVehicle.type || "",
          purchaseDate: apiVehicle.purchasedDate
            ? apiVehicle.purchasedDate.split("T")[0]
            : vehicleConfigurationRef.current?.purchaseDate
              ? vehicleConfigurationRef.current.purchaseDate.split("T")[0]
              : configPurchaseDate,
          dailyMileage: apiVehicle.distanceOneWay?.toString() || vehicleConfigurationRef.current?.dailyMileage?.toString() || "10",
          annualMileage: apiVehicle.estimatedAnnualDistance?.toString() || vehicleConfigurationRef.current?.annualMileage?.toString() || "12000",
          databaseId: apiVehicle.databaseId || "",
          comprehensiveDeductible: apiVehicle.deductibleComprehensive || vehicleConfigurationRef.current?.comprehensiveDeductible || "",
          collisionDeductible: apiVehicle.deductibleCollision || vehicleConfigurationRef.current?.collisionDeductible || "",
          rentalLimit: vehicleConfigurationRef.current?.rentalLimit || "",
          rentalDeductible: "",
          towingLaborLimit: vehicleConfigurationRef.current?.towingAndLaborLimit || "",
          towingLaborDeductible: "",
          customEquipmentLimit: vehicleConfigurationRef.current?.customEquipmentLimit || "",
          customEquipmentDeductible: "",
          gapLimit: vehicleConfigurationRef.current?.gap || "",
          gapDeductible: "",
          milesToWork: apiVehicle.milesToWork || vehicleConfigurationRef.current?.milesToWork || "",
          usage: (() => {
            const usageMap: Record<string, number> = {
              "Artisan Contractor": 0,
              Business: 1,
              Farm: 4,
              Pleasure: 7,
              "Driven To and From School": 3,
            };
            return usageMap[apiVehicle.vehicleCodeUse] ?? vehicleConfigurationRef.current?.usage ?? "";
          })(),

          telematics: apiVehicle.telematics || vehicleConfigurationRef.current?.telematics || "",
          assignedDriverId: "",
          safetyGlassCoverage: vehicleConfigurationRef.current?.safetyGlassCoverage || "",
        }));
        const databaseIds = mappedVehicles.map((v: any) => v.databaseId);

        getVehicleCoverageListByDatabaseId(databaseIds);

        setVehicles(mappedVehicles);
        setNextVehicleId(mappedVehicles.length + 1);

        // Update coverage data from the first vehicle's coverage values
        // if (mappedVehicles.length > 0) {
        //   const firstVehicle = mappedVehicles[0];
        //   setCoverageData((prev) =>
        //     prev.map((coverage) => {
        //       if (coverage.name === "Comprehensive") {
        //         return { ...coverage, deductible: firstVehicle.comprehensiveDeductible || coverage.deductible };
        //       }
        //       if (coverage.name === "Collision") {
        //         return { ...coverage, deductible: firstVehicle.collisionDeductible || coverage.deductible };
        //       }
        //       if (coverage.name === "Rental") {
        //         return { ...coverage, limit: firstVehicle.rentalLimit || coverage.limit };
        //       }
        //       if (coverage.name === "Towing and Labor") {
        //         return { ...coverage, limit: firstVehicle.towingLaborLimit || coverage.limit };
        //       }
        //       if (coverage.name === "Custom Equipment") {
        //         return { ...coverage, limit: firstVehicle.customEquipmentLimit || coverage.limit };

        mappedVehicles.forEach((vehicle: any) => {
          // Fetch models if year and make are available
          if (vehicle.year && vehicle.make) {
            fetchModels(vehicle.id, vehicle.year, vehicle.make);
          }

          // Decode VIN if needed
          if (vehicle.vin && vehicle.vin.length === 17 && (!vehicle.year || !vehicle.make || !vehicle.model)) {
            decodeVINByString(vehicle.id, vehicle.vin);
          }
        });
      })
      .catch((error: any) => {
        setVehicleLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "An error occurred while searching the client!"}`
                : "Unknown error while getting  the vehicle list details!",
            life: 3000,
          });
        }
      });
  };
  const getVehicleCoverageListByDatabaseId = (databaseIds: string[]) => {
    let data = JSON.stringify({
      insuredDataBaseId: databaseIds,
    });
    axios({
      method: "post",
      url: `${process.env.REACT_APP_ACE_API_Url}/api/ace/vehiclecoverage?ncToken=${localStorage.getItem("NCToken")}  `,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        "Content-Type": "Application/json",
      },
      data: data,
    })
      .then((response: any) => {
        setVehicleLoading(false);

        const coverageData = response.data;

        if (Array.isArray(coverageData)) {
          setVehicles((prev) =>
            prev.map((vehicle, index) => {
              const vehicleCoverageBlock = coverageData[index];

              if (!Array.isArray(vehicleCoverageBlock) || !vehicleCoverageBlock[0]) {
                // No Coverage data from API, use configuration values
                return {
                  ...vehicle,
                  rentalLimit: vehicle.rentalLimit || vehicleConfiguration?.rentalLimit || "",
                  towingLaborLimit: vehicle.towingLaborLimit || vehicleConfiguration?.towingAndLaborLimit || "",
                  customEquipmentLimit: vehicle.customEquipmentLimit || vehicleConfiguration?.customEquipmentLimit || "",
                  gapLimit: vehicle.gapLimit || vehicleConfiguration?.gap || "",
                };
              }

              const vehicleSpecificCoverages = vehicleCoverageBlock[0].vehicleSpecificCoverages || [];

              const rental = vehicleSpecificCoverages.find((c: any) => c.typeCode === "RREIM");
              const towing = vehicleSpecificCoverages.find((c: any) => c.typeCode === "TL");
              const customEquip = vehicleSpecificCoverages.find((c: any) => c.typeCode === "AVD");
              const gap = vehicleSpecificCoverages.find((c: any) => c.typeCode === "GAP");
              const rentalRaw = rental?.limits?.[0];
              const towingRaw = towing?.limits?.[0];
              const customEquipRaw = customEquip?.limits?.[0];
              return {
                ...vehicle,
                rentalLimit:
                  rentalRaw === 0
                    ? "None"
                    : (rentalRaw ? rentalRaw.toString() : "") || vehicle.rentalLimit || vehicleConfiguration?.rentalLimit || "",
                towingLaborLimit:
                  towingRaw === 0
                    ? "None"
                    : (towingRaw ? towingRaw.toString() : "") || vehicle.towingLaborLimit || vehicleConfiguration?.towingAndLaborLimit || "",
                customEquipmentLimit:
                  customEquipRaw === 0
                    ? "None"
                    : (customEquipRaw ? customEquipRaw.toString() : "") ||
                      vehicle.customEquipmentLimit ||
                      vehicleConfiguration?.customEquipmentLimit ||
                      "",
                gapLimit: gap?.limitFormatted || vehicle.gapLimit || vehicleConfiguration?.gap || "",
              };
            }),
          );
        }
      })
      .catch((error: any) => {
        setVehicleLoading(false);
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/");
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response && error.response.status
                ? `Error ${error.response.status}: ${error.response.data?.message || "Unknown error while getting the vehicle coverage details!"}`
                : "Unknown error while getting the vehicle coverage details!",
            life: 3000,
          });
        }
      });
  };
  const getValidOptions = (fieldName: string, state: string): string[] => {
    switch (fieldName) {
      case "rentalLimit":
        return ["None", "20", "25", "30", "35", "40", "50"];
      case "towingLaborLimit":
        return ["None", "20", "30", "35", "40", "50", "70", "75", "100"];
      case "comprehensiveDeductible":
      case "collisionDeductible":
        return [
          "None",
          "0",
          "50",
          "100",
          "150",
          "200",
          "250",
          "300",
          "350",
          "400",
          "450",
          "475",
          "500",
          "550",
          "600",
          "700",
          "750",
          "800",
          "975",
          "1000",
          "1500",
          "2000",
          "2500",
          "3000",
          "5000",
        ];
      case "gapLimit":
        return ["Yes", "No"];
      case "safetyGlassCoverage":
        return ["Yes", "No"];
      default:
        return [];
    }
  };
  const isValidDropdownValue = (fieldName: string, value: string, state: string): boolean => {
    if (!value) return false;
    // Convert to string in case it's a number
    const stringValue = String(value).trim();
    if (stringValue === "") return false;
    const validOptions = getValidOptions(fieldName, state);
    return validOptions.includes(stringValue);
  };
  const validateVehicles = (): boolean => {
    const errors: { [key: string]: string } = {};
    if (isNonOwnerPolicy) {
      // No validation needed for non-owner policy
      setValidationErrors({});
      return true;
    }

    vehicles.forEach((vehicle, index) => {
      // No validation for Miles to Work (handled by input restriction)
      const prefix = `vehicle-${vehicle.id}`;

      // Required field validations
      if (!vehicle.year || vehicle.year.trim() === "") {
        errors[`${prefix}-year`] = "Year is required";
      }

      if (!vehicle.make || vehicle.make.trim() === "") {
        errors[`${prefix}-make`] = "Make is required";
      }

      if (!vehicle.model || vehicle.model.trim() === "") {
        errors[`${prefix}-model`] = "Model is required";
      }

      // State-specific coverage validations
      const state = selectedState || "";

      // Always required - check existence only (config values are valid)
      if (!vehicle.comprehensiveDeductible) {
        errors[`${prefix}-comprehensiveDeductible`] = "Comprehensive deductible is required";
      }

      if (!vehicle.collisionDeductible) {
        errors[`${prefix}-collisionDeductible`] = "Collision deductible is required";
      }

      if (!vehicle.rentalLimit) {
        errors[`${prefix}-rentalLimit`] = "Rental limit is required";
      } else if (!isValidDropdownValue("rentalLimit", vehicle.rentalLimit, state)) {
        errors[`${prefix}-rentalLimit`] = "Please select a valid rental limit option from the dropdown";
      }

      if (!vehicle.towingLaborLimit) {
        errors[`${prefix}-towingLaborLimit`] = "Towing and labor limit is required";
      } else if (!isValidDropdownValue("towingLaborLimit", vehicle.towingLaborLimit, state)) {
        errors[`${prefix}-towingLaborLimit`] = "Please select a valid towing and labor limit option from the dropdown";
      }

      if (!vehicle.gapLimit) {
        errors[`${prefix}-gapLimit`] = "GAP coverage is required";
      } else if (!isValidDropdownValue("gapLimit", vehicle.gapLimit, state)) {
        errors[`${prefix}-gapLimit`] = "Please select a valid GAP coverage option from the dropdown";
      }

      // Custom Equipment: Only required for AL, GA
      if (["Alabama", "Georgia"].includes(state)) {
        if (!vehicle.customEquipmentLimit) {
          errors[`${prefix}-customEquipmentLimit`] = "Custom equipment limit is required";
        }
        // Note: Custom equipment is a number input, not dropdown, so no validity check needed
      }

      // Safety Glass Coverage: Only required for AL, FL, SC, GA
      if (["Alabama", "Florida", "South Carolina", "Georgia"].includes(state)) {
        if (!vehicle.safetyGlassCoverage) {
          errors[`${prefix}-safetyGlassCoverage`] = "Safety glass coverage is required";
        } else if (!isValidDropdownValue("safetyGlassCoverage", vehicle.safetyGlassCoverage, state)) {
          errors[`${prefix}-safetyGlassCoverage`] = "Please select a valid safety glass coverage option from the dropdown";
        }
      }
    });

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Show error toast
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please fill in all required fields and correct any errors.",
        life: 4000,
      });

      // Find first vehicle with errors and expand it
      const firstErrorKey = Object.keys(errors)[0];
      const vehicleIdMatch = firstErrorKey.match(/vehicle-(\d+)/);
      if (vehicleIdMatch) {
        const vehicleId = parseInt(vehicleIdMatch[1]);
        setVehicles((prev) =>
          prev.map((v) => ({
            ...v,
            isExpanded: v.id === vehicleId ? true : v.isExpanded,
          })),
        );

        // Scroll to the vehicle with errors
        setTimeout(() => {
          const card = document.querySelector(`[data-vehicle-id="${vehicleId}"]`);
          if (card) {
            card.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }

      return false;
    }

    return true;
  };
  const handleEditVehicle = (id: number) => {
    setVehicles((prev) =>
      prev.map((vehicle) => {
        if (vehicle.id === id) {
          // Only expand if not already expanded
          return { ...vehicle, isExpanded: true };
        }
        return { ...vehicle, isExpanded: false };
      }),
    );
    // Only scroll if user is not actively typing
    if (!isUserInteractingRef.current) {
      // Wait for DOM update before scrolling
      requestAnimationFrame(() => {
        setTimeout(() => {
          const card = document.querySelector(`[data-vehicle-id="${id}"]`);
          if (card) card.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      });
    }
  };

  const toggleVehicleExpanded = (id: number) => {
    setVehicles((prev) =>
      prev.map((vehicle) => {
        if (vehicle.id === id) {
          // Toggle the clicked vehicle
          return { ...vehicle, isExpanded: !vehicle.isExpanded };
        } else {
          // Collapse all other vehicles
          return { ...vehicle, isExpanded: false };
        }
      }),
    );
  };

  const handleInputChange = (id: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Save scroll position and focused element before state update
    const layoutContent = document.querySelector(".layout-content");
    const savedScrollTop = layoutContent ? layoutContent.scrollTop : 0;
    const activeElement = document.activeElement as HTMLElement;

    // Update vehicle data
    setVehicles((prev) =>
      prev.map((vehicle) => {
        if (vehicle.id === id) {
          const updatedVehicle = { ...vehicle, [name]: value };

          // If year or make changed, fetch models
          if (name === "year" || name === "make") {
            const year = name === "year" ? value : vehicle.year;
            const make = name === "make" ? value : vehicle.make;

            if (year && make) {
              fetchModels(id, year, make);
            }

            // Clear model if year or make changed
            if (name === "year" || name === "make") {
              updatedVehicle.model = "";
            }
          }

          return updatedVehicle;
        }
        return vehicle;
      }),
    );

    // Clear validation error for this field
    const errorKey = `vehicle-${id}-${name}`;
    setValidationErrors((prev) => {
      if (prev[errorKey]) {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      }
      return prev;
    });

    // Restore scroll position immediately after state update
    requestAnimationFrame(() => {
      if (layoutContent) {
        layoutContent.scrollTop = savedScrollTop;
      }
      // Restore focus if it was lost
      if (activeElement && document.activeElement !== activeElement) {
        activeElement.focus();
      }
    });
  };

  const getVehicleCoverageData = (vehicle: VehicleData): CoverageRow[] => {
    const baseRows: CoverageRow[] = [
      { id: 1, name: "Comprehensive", limit: "ACV", deductible: vehicle.comprehensiveDeductible || "" },
      { id: 2, name: "Collision", limit: "ACV", deductible: vehicle.collisionDeductible || "" },
      { id: 3, name: "Rental", limit: vehicle.rentalLimit || "", deductible: "" },
      { id: 4, name: "Towing and Labor", limit: vehicle.towingLaborLimit || "", deductible: "" },
      { id: 5, name: "GAP", limit: vehicle.gapLimit || "", deductible: "" },
    ];
    let nextId = 6;
    const rows = [...baseRows];
    if (["Alabama", "Georgia"].includes(selectedState || "")) {
      rows.push({ id: nextId++, name: "Custom Equipment", limit: vehicle.customEquipmentLimit || "", deductible: "" });
    }
    if (["Alabama", "Florida", "South Carolina", "Georgia"].includes(selectedState || "")) {
      rows.push({ id: nextId++, name: "Safety Glass Coverage", limit: vehicle.safetyGlassCoverage || "", deductible: "" });
    }
    return rows;
  };

  const handleVehicleCoverageChange = (vehicleId: number, coverageName: string, field: "limit" | "deductible", value: string) => {
    // Save scroll position and focused element before state update
    const layoutContent = document.querySelector(".layout-content");
    const savedScrollTop = layoutContent ? layoutContent.scrollTop : 0;
    const activeElement = document.activeElement as HTMLElement;

    setVehicles((prev) =>
      prev.map((vehicle) => {
        if (vehicle.id !== vehicleId) return vehicle;

        // Update the appropriate field based on coverage name
        switch (coverageName) {
          case "Comprehensive":
            return { ...vehicle, comprehensiveDeductible: value };
          case "Collision":
            return { ...vehicle, collisionDeductible: value };
          case "Rental":
            return { ...vehicle, rentalLimit: value };
          case "Towing and Labor":
            return { ...vehicle, towingLaborLimit: value };
          case "Custom Equipment":
            return { ...vehicle, customEquipmentLimit: value };
          case "GAP":
            return { ...vehicle, gapLimit: value };
          case "Safety Glass Coverage":
            return { ...vehicle, safetyGlassCoverage: value };
          default:
            return vehicle;
        }
      }),
    );

    // Clear validation error for this coverage field
    const fieldNameMap: { [key: string]: string } = {
      Comprehensive: "comprehensiveDeductible",
      Collision: "collisionDeductible",
      Rental: "rentalLimit",
      "Towing and Labor": "towingLaborLimit",
      "Custom Equipment": "customEquipmentLimit",
      GAP: "gapLimit",
      "Safety Glass Coverage": "safetyGlassCoverage",
    };

    const fieldName = fieldNameMap[coverageName];
    if (fieldName) {
      const errorKey = `vehicle-${vehicleId}-${fieldName}`;
      setValidationErrors((prev) => {
        if (prev[errorKey]) {
          const newErrors = { ...prev };
          delete newErrors[errorKey];
          return newErrors;
        }
        return prev;
      });
    }

    // Restore scroll position and focus immediately after state update
    requestAnimationFrame(() => {
      if (layoutContent) {
        layoutContent.scrollTop = savedScrollTop;
      }
      // Restore focus if it was lost
      if (activeElement && document.activeElement !== activeElement) {
        activeElement.focus();
      }
    });
  };

  const deductibleTemplate = (row: any, vehicleId: number) => {
    // Only show Deductible for Comprehensive and Collision, with required asterisk
    let deductibleOptions: { label: string; value: string }[] = [];
    if (row.name === "Collision" || row.name === "Comprehensive") {
      deductibleOptions = [
        { label: "None", value: "None" },
        { label: "0", value: "0" },
        { label: "50", value: "50" },
        { label: "100", value: "100" },
        { label: "150", value: "150" },
        { label: "200", value: "200" },
        { label: "250", value: "250" },
        { label: "300", value: "300" },
        { label: "350", value: "350" },
        { label: "400", value: "400" },
        { label: "450", value: "450" },
        { label: "475", value: "475" },
        { label: "500", value: "500" },
        { label: "550", value: "550" },
        { label: "600", value: "600" },
        { label: "700", value: "700" },
        { label: "750", value: "750" },
        { label: "800", value: "800" },
        { label: "975", value: "975" },
        { label: "1000", value: "1000" },
        { label: "1500", value: "1500" },
        { label: "2000", value: "2000" },
        { label: "2500", value: "2500" },
        { label: "3000", value: "3000" },
        { label: "5000", value: "5000" },
      ];
    }
    if (["Collision", "Comprehensive"].includes(row.name)) {
      const fieldName = row.name === "Comprehensive" ? "comprehensiveDeductible" : "collisionDeductible";
      const errorKey = `vehicle-${vehicleId}-${fieldName}`;

      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", minHeight: validationErrors[errorKey] ? "60px" : "auto" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <select
              value={row.deductible}
              onChange={(e) => handleVehicleCoverageChange(vehicleId, row.name, "deductible", e.target.value)}
              className={`w-full compact-select table-input ${validationErrors[errorKey] ? "error" : ""}`}
            >
              <option value="">Select</option>
              {deductibleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="text-danger" style={{ marginLeft: 4, flexShrink: 0 }}>
              *
            </span>
          </div>
          {validationErrors[errorKey] && <span className="error-message">{validationErrors[errorKey]}</span>}
        </div>
      );
    }

    return null;
  };

  const limitTemplate = (row: CoverageRow, vehicleId: number) => {
    // For Comprehensive and Collision, show nothing
    if (row.name === "Comprehensive" || row.name === "Collision") {
      return null;
    }

    const options: Record<string, { label: string; value: string }[]> = {
      Rental: [
        { label: "None", value: "None" },
        { label: "20", value: "20" },
        { label: "25", value: "25" },
        { label: "30", value: "30" },
        { label: "35", value: "35" },
        { label: "40", value: "40" },
        { label: "50", value: "50" },
      ],
      "Towing and Labor": [
        { label: "None", value: "None" },
        { label: "20", value: "20" },
        { label: "30", value: "30" },
        { label: "35", value: "35" },
        { label: "40", value: "40" },
        { label: "50", value: "50" },
        { label: "70", value: "70" },
        { label: "75", value: "75" },
        { label: "100", value: "100" },
      ],
      "Custom Equipment": [
        // CustomEquipmentValue: min 0, max 99999
        // We'll use a number input for this field, not dropdown
      ],
      GAP: [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
      "Safety Glass Coverage": [
        { label: "Yes", value: "Yes" },
        { label: "No", value: "No" },
      ],
    };

    // Add required asterisk for Rental, Towing and Labor, Custom Equipment, GAP, Safety Glass Coverage
    if (["Rental", "Towing and Labor", "GAP", "Safety Glass Coverage"].includes(row.name)) {
      const fieldNameMap: Record<string, string> = {
        Rental: "rentalLimit",
        "Towing and Labor": "towingLaborLimit",
        GAP: "gapLimit",
        "Safety Glass Coverage": "safetyGlassCoverage",
      };
      const fieldName = fieldNameMap[row.name];
      const errorKey = `vehicle-${vehicleId}-${fieldName}`;
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", minHeight: validationErrors[errorKey] ? "60px" : "auto" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <select
              value={row.limit}
              onChange={(e) => handleVehicleCoverageChange(vehicleId, row.name, "limit", e.target.value)}
              className={`w-full compact-select table-input ${validationErrors[errorKey] ? "error" : ""}`}
            >
              <option value="">Select</option>
              {options[row.name]?.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="text-danger" style={{ marginLeft: 4, flexShrink: 0 }}>
              *
            </span>
          </div>
          {validationErrors[errorKey] && <span className="error-message">{validationErrors[errorKey]}</span>}
        </div>
      );
    }
    if (row.name === "Custom Equipment") {
      const fieldName = "customEquipmentLimit";
      const errorKey = `vehicle-${vehicleId}-${fieldName}`;
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%", minHeight: validationErrors[errorKey] ? "60px" : "auto" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="number"
              min={0}
              max={99999}
              value={row.limit || ""}
              onChange={(e) => {
                let val = e.target.value;
                let num = Number(val);
                if (val === "" || (!isNaN(num) && num >= 0 && num <= 99999)) {
                  handleVehicleCoverageChange(vehicleId, row.name, "limit", val);
                }
              }}
              className={`w-full compact-select table-input ${validationErrors[errorKey] ? "error" : ""}`}
              placeholder="Enter custom equipment value"
            />
            <span className="text-danger" style={{ marginLeft: 4, flexShrink: 0 }}>
              *
            </span>
          </div>
          {validationErrors[errorKey] && <span className="error-message">{validationErrors[errorKey]}</span>}
        </div>
      );
    }
    return null;
  };

  const mergeVehiclesWithCoverageData = () => {
    // Vehicles already have all coverage data, just return them
    return vehicles;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Require at least 1 vehicle for Standard policy
    if (policyType === "Standard" && vehicles.length === 0) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "At least one vehicle is required for a Standard policy.",
        life: 4000,
      });
      return;
    }
    // Submit logic will be implemented here
  };

  const handleAddVehicle = useCallback(() => {
    if (isNonOwnerPolicy) return;
    // Clear any pending timeouts
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    if (expandTimeoutRef.current) clearTimeout(expandTimeoutRef.current);

    const newVehicle: VehicleData = {
      id: nextVehicleId,
      isExpanded: false,
      vin: "",
      year: "",
      make: "",
      model: "",
      purchaseDate: vehicleConfiguration?.purchaseDate ? vehicleConfiguration.purchaseDate.split("T")[0] : configPurchaseDate || "",
      dailyMileage: vehicleConfiguration?.dailyMileage?.toString() || "10",
      annualMileage: vehicleConfiguration?.annualMileage?.toString() || "12000",
      comprehensiveDeductible: vehicleConfiguration?.comprehensiveDeductible || "",
      collisionDeductible: vehicleConfiguration?.collisionDeductible || "",
      rentalLimit: vehicleConfiguration?.rentalLimit || "",
      rentalDeductible: "",
      towingLaborLimit: vehicleConfiguration?.towingAndLaborLimit || "",
      towingLaborDeductible: "",
      customEquipmentLimit: vehicleConfiguration?.customEquipmentLimit || "",
      customEquipmentDeductible: "",
      gapLimit: vehicleConfiguration?.gap || "",
      gapDeductible: "",
      usage: vehicleConfiguration?.usage || "",
      milesToWork: vehicleConfiguration?.milesToWork || "",
      assignedDriverId: "",
      telematics: vehicleConfiguration?.telematics || "",
      safetyGlassCoverage: vehicleConfiguration?.safetyGlassCoverage || "",
    };

    setVehicles((prev) => [...prev.map((v) => ({ ...v, isExpanded: false })), newVehicle]);
    const currentVehicleId = nextVehicleId;
    setNextVehicleId((prev) => prev + 1);

    scrollTimeoutRef.current = setTimeout(() => {
      const newVehicleCard = document.querySelector(`[data-vehicle-id="${currentVehicleId}"]`);
      if (newVehicleCard) {
        newVehicleCard.scrollIntoView({ behavior: "smooth", block: "start" });
        // Expand after scrolling
        expandTimeoutRef.current = setTimeout(() => {
          setVehicles((prev) => prev.map((v) => (v.id === currentVehicleId ? { ...v, isExpanded: true } : { ...v, isExpanded: false })));
        }, 300);
      }
    }, 100);
  }, [nextVehicleId, vehicleConfiguration, configPurchaseDate, isNonOwnerPolicy]);

  const handleRemoveVehicle = useCallback(
    (id: number) => {
      if (isNonOwnerPolicy) return;
      setVehicles((prev) => {
        if (prev.length > 1) {
          return prev.filter((vehicle) => vehicle.id !== id);
        }
        return prev;
      });
    },
    [isNonOwnerPolicy],
  );

  const handleCheckVIN = async (id: number) => {
    const vehicle = vehicles.find((v) => v.id === id);
    if (!vehicle || !vehicle.vin || vehicle.vin.length !== 17) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_ACE_API_Url}/api/ace/vindetails?vin=${vehicle.vin}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        },
      );
      const data = await response.json();

      const year = data.year ? String(data.year) : "";
      const make = data.maker || "";
      const model = data.model || "";

      if (!year && !make && !model) {
        return;
      }

      setVehicles((prev) =>
        prev.map((v) =>
          v.id === id
            ? {
                ...v,
                year: year || v.year,
                make: make ? make.toUpperCase() : v.make,
                model: model || v.model,
              }
            : v,
        ),
      );

      // Fetch models for the decoded make and year
      if (year && make) {
        fetchModels(id, year, make);
      }
    } catch (error) {
      console.error("Error decoding VIN:", error);
    }
  };

  // Render JSX based on whether it's in a workflow or standalone
  const renderContent = () => (
    <div className="page-container" style={{ position: "relative" }}>
      <Toast ref={toast} />
      {/* Loading Overlay */}
      {vehicleLoading && (
        <div className="driver-loading-overlay">
          <div className="driver-loading-content">
            <div className="driver-loading-spinner" />
            <p className="driver-loading-text">Loading vehicle information...</p>
          </div>
        </div>
      )}
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-title-section">
            <h1>Vehicle Information</h1>
            <span className="title-separator">|</span>
            <p>
              Enter and manage vehicle details for the policy ({vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""})
            </p>
          </div>
        </div>
      </div>

      {/* Non-owner policy selection */}

      <form onSubmit={handleSubmit} className="vehicle-form">
        <div className="form-group" style={{ width: "100%", marginBottom: 24 }}>
          <label htmlFor="non-owner-toggle">Non-owner policy?</label>
          <InputSwitch id="non-owner-toggle" checked={isNonOwnerPolicy} disabled style={{ marginLeft: 0, marginRight: 0 }} />
        </div>
        <div className="form-container">
          {isNonOwnerPolicy ? (
            <div
              className="warning-msg"
              style={{ textAlign: "left", justifyContent: "flex-start", alignItems: "flex-start", display: "flex", width: "100%" }}
            >
              <span style={{ width: "100%" }}>No vehicles can be added for a Non-owner policy.</span>
            </div>
          ) : (
            <>
              {vehicles.length > 0 && (
                <div className="" style={{ marginBottom: "2rem" }}>
                  <DataTable
                    value={vehicles}
                    className="custom-datatable"
                    showGridlines
                    responsiveLayout="scroll"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Column field="id" header="#" body={(rowData, options) => options.rowIndex + 1} style={{ width: "40px" }} />
                    <Column field="vin" header="VIN" />
                    <Column field="year" header="Year" />
                    <Column field="make" header="Make" />
                    <Column field="model" header="Model" />
                    <Column
                      header="Comp and Collision"
                      body={(rowData) => {
                        const comp = rowData.comprehensiveDeductible;
                        const coll = rowData.collisionDeductible;
                        if (!comp && !coll) return "";
                        return `${comp || "-"} / ${coll || "-"}`;
                      }}
                    />
                    <Column
                      header="Actions"
                      body={(rowData) => (
                        <span>
                          <button
                            type="button"
                            title="Edit vehicle"
                            style={{ background: "none", border: "none", cursor: "pointer", marginRight: "8px" }}
                            onClick={() => handleEditVehicle(rowData.id)}
                            disabled={isNonOwnerPolicy}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M4 21v-3.5a2 2 0 0 1 .59-1.41l10.83-10.83a2 2 0 0 1 2.83 0l1.91 1.91a2 2 0 0 1 0 2.83L9.33 20.41A2 2 0 0 1 7.92 21H4z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {vehicles.length > 1 && (
                            <button
                              type="button"
                              title="Remove vehicle"
                              style={{ background: "none", border: "none", cursor: "pointer" }}
                              onClick={() => handleRemoveVehicle(rowData.id)}
                              disabled={isNonOwnerPolicy}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                              </svg>
                            </button>
                          )}
                        </span>
                      )}
                      style={{ width: "90px", textAlign: "center" }}
                    />
                  </DataTable>
                </div>
              )}

              {/* Render each vehicle as a collapsible card */}
              {vehicles.map((vehicle, index) => (
                <div key={vehicle.id} className="vehicle-card-section" data-vehicle-id={vehicle.id}>
                  <div className="driver-card-header" onClick={() => toggleVehicleExpanded(vehicle.id)}>
                    <div className="driver-card-title">
                      <svg viewBox="0 0 24 24" fill="none" style={{ width: "20px", height: "20px" }}>
                        <path d="M8 16h8M3 12l3-6h12l3 6-3 6H6l-3-6z" stroke="currentColor" strokeWidth="2" />
                        <circle cx="7" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="17" cy="15" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                      <h3>
                        Vehicle #{index + 1}
                        {vehicle.year && vehicle.make && vehicle.model && ` - ${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      </h3>
                    </div>
                    <div className="driver-card-actions" onClick={(e) => e.stopPropagation()}>
                      {vehicles.length > 1 && (
                        <button
                          type="button"
                          className="btn-remove-driver"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveVehicle(vehicle.id);
                          }}
                          title="Remove vehicle"
                        >
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        className={`btn-toggle-driver ${vehicle.isExpanded ? "expanded" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleVehicleExpanded(vehicle.id);
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M19 9l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className={`vehicle-card-content ${vehicle.isExpanded ? "expanded" : "collapsed"}`}>
                    {/* Basic Vehicle Information */}
                    <div className="form-section-header" id={`vehicle-basic-info-${vehicle.id}`}>
                      <h3>Basic Vehicle Information</h3>
                    </div>

                    <div className="form-row">
                      <div className="form-group" style={{ position: "relative" }}>
                        <label htmlFor={`vin-${vehicle.id}`}>VIN</label>
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                          <input
                            type="text"
                            id={`vin-${vehicle.id}`}
                            name="vin"
                            value={vehicle.vin}
                            onChange={(e) => handleInputChange(vehicle.id, e)}
                            placeholder="17-character VIN"
                            className={`form-input ${validationErrors[`vehicle-${vehicle.id}-vin`] ? "error" : ""}`}
                            maxLength={17}
                            style={{ flex: 1 }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleCheckVIN(vehicle.id);
                              }
                            }}
                            onBlur={() => handleCheckVIN(vehicle.id)}
                          />
                          {/* <button
                            type="button"
                            onClick={() => handleCheckVIN(vehicle.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCheckVIN(vehicle.id);
                              }
                            }}
                            className="btn btn-secondary"
                            title="Decode VIN to auto-populate vehicle details"
                          >
                            Check VIN
                          </button> */}
                        </div>
                        {validationErrors[`vehicle-${vehicle.id}-vin`] && (
                          <span className="error-message">{validationErrors[`vehicle-${vehicle.id}-vin`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`year-${vehicle.id}`}>
                          Year <span className="text-danger">*</span>
                        </label>
                        <select
                          id={`year-${vehicle.id}`}
                          name="year"
                          value={vehicle.year}
                          onChange={(e) => handleInputChange(vehicle.id, e)}
                          className={`form-input compact-select ${validationErrors[`vehicle-${vehicle.id}-year`] ? "error" : ""}`}
                        >
                          <option value="">Select Year</option>
                          {Array.from({ length: 60 }, (_, i) => {
                            const yearStr = (2025 - i).toString();
                            return (
                              <option key={yearStr} value={yearStr}>
                                {yearStr}
                              </option>
                            );
                          })}
                        </select>
                        {validationErrors[`vehicle-${vehicle.id}-year`] && (
                          <span className="error-message">{validationErrors[`vehicle-${vehicle.id}-year`]}</span>
                        )}
                      </div>

                      <div className="form-group">
                        <label htmlFor={`make-${vehicle.id}`}>
                          Make <span className="text-danger">*</span>
                        </label>
                        <select
                          id={`make-${vehicle.id}`}
                          name="make"
                          value={vehicle.make}
                          onChange={(e) => handleInputChange(vehicle.id, e)}
                          className={`form-input compact-select ${validationErrors[`vehicle-${vehicle.id}-make`] ? "error" : ""}`}
                        >
                          <option value="">Select Make</option>
                          {VEHICLE_MAKES.map((make) => (
                            <option key={make} value={make}>
                              {make.charAt(0).toUpperCase() + make.slice(1)}
                            </option>
                          ))}
                        </select>
                        {validationErrors[`vehicle-${vehicle.id}-make`] && (
                          <span className="error-message">{validationErrors[`vehicle-${vehicle.id}-make`]}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor={`model-${vehicle.id}`}>
                          Model <span className="text-danger">*</span>
                        </label>
                        <select
                          id={`model-${vehicle.id}`}
                          name="model"
                          value={vehicle.model}
                          onChange={(e) => handleInputChange(vehicle.id, e)}
                          className={`form-input compact-select ${validationErrors[`vehicle-${vehicle.id}-model`] ? "error" : ""}`}
                          disabled={!vehicle.year || !vehicle.make}
                        >
                          <option value="">{!vehicle.year || !vehicle.make ? "Select Year & Make First" : "Select Model"}</option>
                          {vehicle.year && vehicle.make && vehicleModels[vehicle.id] && vehicleModels[vehicle.id].length > 0 ? (
                            vehicleModels[vehicle.id].map((model) => (
                              <option key={model} value={model}>
                                {model}
                              </option>
                            ))
                          ) : vehicle.year && vehicle.make ? (
                            <option value="" disabled>
                              Loading models...
                            </option>
                          ) : null}
                        </select>
                        {validationErrors[`vehicle-${vehicle.id}-model`] && (
                          <span className="error-message">{validationErrors[`vehicle-${vehicle.id}-model`]}</span>
                        )}
                      </div>

                      {/* Move Usage field immediately after Model */}
                      {renderUsageDropdown(vehicle)}

                      {/* Miles to Work (all states) */}
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`milesToWork-${vehicle.id}`}>Miles to Work</label>
                        <input
                          type="number"
                          id={`milesToWork-${vehicle.id}`}
                          name="milesToWork"
                          value={vehicle.milesToWork || ""}
                          onChange={(e) => {
                            let val = e.target.value;
                            // Prevent entering less than 0 or more than 999
                            if (val === "") {
                              handleInputChange(vehicle.id, e);
                            } else {
                              let num = Number(val);
                              if (!isNaN(num)) {
                                if (num < 0) num = 0;
                                if (num > 999) num = 999;
                                e.target.value = String(num);
                                handleInputChange(vehicle.id, e);
                              }
                            }
                          }}
                          placeholder="Enter miles to work"
                          className="form-input"
                          min="0"
                          max="999"
                        />
                        {/* No error message for Miles to Work, input is restricted */}
                      </div>

                      {/* Annual Mileage (all states) */}
                      <div className="form-group">
                        <label htmlFor={`annualMileage-${vehicle.id}`}>Annual Mileage</label>
                        <input
                          type="number"
                          id={`annualMileage-${vehicle.id}`}
                          name="annualMileage"
                          value={vehicle.annualMileage || ""}
                          onChange={(e) => {
                            let val = e.target.value;
                            if (val === "") {
                              handleInputChange(vehicle.id, e);
                            } else {
                              let num = Number(val);
                              if (!isNaN(num)) {
                                if (num < 0) num = 0;
                                if (num > 1000000) num = 1000000;
                                e.target.value = String(num);
                                handleInputChange(vehicle.id, e);
                              }
                            }
                          }}
                          placeholder="Enter annual mileage"
                          className="form-input"
                          min="0"
                          max="1000000"
                        />
                      </div>
                      {(selectedState === "Ohio" || selectedState === "Alabama") && (
                        <>
                          {renderAssignedDriverId(vehicle)}
                          {renderTelematics(vehicle)}
                        </>
                      )}

                      {selectedState === "Florida" && (
                        <>
                          <div className="form-group">
                            <label htmlFor={`assignedDriverId-${vehicle.id}`}>Assigned Driver ID</label>
                            <input
                              type="number"
                              id={`assignedDriverId-${vehicle.id}`}
                              name="assignedDriverId"
                              value={vehicle.assignedDriverId || ""}
                              onChange={(e) => {
                                let val = e.target.value;
                                if (val === "") {
                                  handleInputChange(vehicle.id, e);
                                } else {
                                  let num = Number(val);
                                  if (!isNaN(num)) {
                                    if (num < 1) num = 1;
                                    if (num > 6) num = 6;
                                    e.target.value = String(num);
                                    handleInputChange(vehicle.id, e);
                                  }
                                }
                              }}
                              className="form-input"
                              min={1}
                              max={6}
                            />
                          </div>
                          {renderTelematics(vehicle)}
                        </>
                      )}

                      {selectedState === "South Carolina" && <>{renderTelematics(vehicle)}</>}

                      {selectedState === "Georgia" && (
                        <>
                          {renderAssignedDriverId(vehicle)}
                          {renderTelematics(vehicle)}
                        </>
                      )}

                      {/* Tennessee: Usage, MilesToWork, AssignedDriverId, AnnualMileage, Telematics */}
                      {selectedState === "Tennessee" && (
                        <>
                          {renderAssignedDriverId(vehicle)}
                          {renderTelematics(vehicle)}
                        </>
                      )}
                    </div>

                    {/* Additional Vehicle Fields (Statewise) */}
                    {/* Ohio, Alabama: AssignedDriverId, Usage, MilesToWork, Telematics */}

                    {/* Vehicle Table */}
                    <div className="form-section-header" id={`vehicle-coverage-${vehicle.id}`}>
                      <h3>Vehicle Coverage </h3>
                    </div>

                    <div className="review-content">
                      <DataTable
                        value={getVehicleCoverageData(vehicle)}
                        className="custom-datatable"
                        rowHover
                        key={`coverage-table-${vehicle.id}-${
                          Object.keys(validationErrors).filter((k) => k.startsWith(`vehicle-${vehicle.id}-`)).length
                        }`}
                      >
                        {/* Coverage Type */}
                        <Column field="name" header="Coverage Type" bodyStyle={{ verticalAlign: "top", paddingTop: "0.75rem" }} />

                        {/* Limit */}
                        <Column
                          header={<span>Limit </span>}
                          body={(row) => limitTemplate(row, vehicle.id)}
                          bodyStyle={{ verticalAlign: "top", overflow: "visible" }}
                        />

                        {/* Deductible */}
                        <Column
                          header={<span>Deductible </span>}
                          body={(row) => deductibleTemplate(row, vehicle.id)}
                          bodyStyle={{ verticalAlign: "top", overflow: "visible" }}
                        />
                      </DataTable>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Vehicle Section */}
              <div className="form-section-header">
                <h3>Additional Options</h3>
              </div>

              <div className="add-coverage-section" style={{ marginBottom: "2rem" }}>
                <button type="button" className=" btn btn-secondary" onClick={handleAddVehicle}>
                  Add Vehicle
                </button>
              </div>
            </>
          )}

          {/* Form Actions */}
          {!vehicleLoading && (
            <div className="form-actions">
              {!isFirstStep && (
                <button className="btn btn-secondary" onClick={onPrevious}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
                  </svg>
                  Previous
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                onClick={(e) => {
                  e.preventDefault();
                  if (validateVehicles()) {
                    const completeVehicleData = mergeVehiclesWithCoverageData();
                    const dataToSend = {
                      vehicles: completeVehicleData,
                      isNonOwnerPolicy: isNonOwnerPolicy,
                    };
                    onNext && onNext(dataToSend);
                  }
                }}
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );

  // Return content without Layout if being used in a stepper workflow
  if (onNext || currentStep !== undefined) {
    return renderContent();
  }

  // Return content wrapped in Layout for standalone page
  return <Layout currentPage="Vehicle Information">{renderContent()}</Layout>;
}
